import React from 'react';
import { Check } from 'lucide-react';
import { PERMISSION_LABELS, type Permission } from '../../auth/permissions';

const PERMISSIONS = Object.keys(PERMISSION_LABELS) as Permission[];

/**
 * The 7-permission checklist, shared by the Roles tab's create/edit form and
 * the create-user form's read-only preview of what the selected role grants.
 *
 * Read-only when `onToggle` is omitted — a box still checked or unchecked,
 * just not clickable, so a preview and an editor never drift into looking
 * like two different UI patterns for the same idea.
 */
export function PermissionChecklist({
  selected,
  onToggle,
}: {
  selected: readonly string[];
  onToggle?: (permission: Permission) => void;
}) {
  const editable = Boolean(onToggle);
  return (
    <div className="space-y-1 rounded-lg border border-slate-200 bg-white p-2">
      {PERMISSIONS.map((permission) => {
        const checked = selected.includes(permission);
        return (
          <button
            key={permission}
            type="button"
            disabled={!editable}
            onClick={() => onToggle?.(permission)}
            className={`flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left transition ${
              checked ? 'bg-teal-50' : editable ? 'hover:bg-slate-50' : ''
            } ${editable ? '' : 'cursor-default'}`}
          >
            <span
              className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border ${
                checked ? 'border-teal-500 bg-teal-500' : 'border-slate-300 bg-white'
              }`}
            >
              {checked && <Check className="h-3 w-3 text-white" />}
            </span>
            <span className="text-xs text-slate-700">{PERMISSION_LABELS[permission]}</span>
          </button>
        );
      })}
    </div>
  );
}
